import { useEffect, useMemo, useState, useCallback, useRef, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createPriceMap, getVisibleMonthDates, getVisibleMonthDatesForMobile, getSingleMonthDate, getDateRangeFromVisibleMonthDates } from "../../utils/fareCalendar.js";
import { useFetchFareCalendar } from "./useFetchFareCalendar.js";
import { actions as searchActions } from "../../store/actions.js";
import { SearchBoxContext } from "../SearchBoxContext/index.js";
import { getSearchbox } from "../../store/selectors.js";
const pageStageMap = {
    home: 4,
    "fly-anywhere": 5,
    searchresults: 6,
    "searchresults-outbound": 6,
    "searchresults-inbound": 6
};
export const useFareCalendarEnabled = (segmentIndex, fareCalendarConfig, mode) => {
    const { isMobile, isMeta, onTrackSBExperimentStage, routeName } = useContext(SearchBoxContext);
    const searchbox = useSelector(getSearchbox);
    const searchType = searchbox.type;
    const cabinClass = searchbox.cabinClass;
    const segment = searchbox.searchSegments?.[segmentIndex];
    const hasSingleValidFrom = segment?.from?.length === 1 && (segment.from[0].type === "CITY" || segment.from[0].type === "AIRPORT");
    const hasSingleValidTo = segment?.to?.length === 1 && (segment.to[0].type === "CITY" || segment.to[0].type === "AIRPORT");
    const areDifferentDestinations = useMemo(() => {
        if (!hasSingleValidFrom || !hasSingleValidTo)
            return false;
        const from = segment.from[0];
        const to = segment.to[0];
        if (from.code === to.code)
            return false;
        if (from.type === "AIRPORT" && to.type === "AIRPORT") {
            if (from.city && to.city && from.city === to.city) {
                return false;
            }
        }
        if (from.type === "CITY" && to.type === "AIRPORT") {
            if (from.code === to.city) {
                return false;
            }
        }
        if (from.type === "AIRPORT" && to.type === "CITY") {
            if (from.city === to.code) {
                return false;
            }
        }
        return true;
    }, [hasSingleValidFrom, hasSingleValidTo, segment]);
    const isSupportedType = searchType === "ONEWAY";
    const isFareCalendarFeatureEnabled = mode === "vertical"
        ? fareCalendarConfig?.isFareCalendarMobileEnabled
        : fareCalendarConfig?.isFareCalendarDesktopEnabled;
    const isEconomyClass = cabinClass === "ECONOMY";
    if (isSupportedType && areDifferentDestinations && isEconomyClass) {
        onTrackSBExperimentStage({ name: "flights_web_cat_fare_calendar_oneway", stage: 1 });
        onTrackSBExperimentStage({ name: "flights_web_cat_fare_calendar_oneway", stage: isMobile ? 3 : 2 });
        onTrackSBExperimentStage({ name: "flights_web_cat_fare_calendar_data_collection", stage: 1 });
        onTrackSBExperimentStage({ name: "flights_web_cat_fare_calendar_data_collection", stage: isMobile ? 3 : 2 });
        if (routeName) {
            onTrackSBExperimentStage({ name: "flights_web_cat_fare_calendar_oneway", stage: pageStageMap[routeName] ?? 7 });
            onTrackSBExperimentStage({
                name: "flights_web_cat_fare_calendar_data_collection",
                stage: pageStageMap[routeName] ?? 7
            });
        }
        onTrackSBExperimentStage({ name: "flights_web_cat_fare_calendar_oneway", stage: isMeta ? 9 : 8 });
        onTrackSBExperimentStage({
            name: "flights_web_cat_fare_calendar_data_collection",
            stage: searchType === "ONEWAY" ? 8 : 9
        });
    }
    return isSupportedType && areDifferentDestinations && isEconomyClass && Boolean(isFareCalendarFeatureEnabled);
};
export const useFareCalendar = ({ baseDate, mode, enabled = true, segmentIndex = 0, singleMonth = false, disableAutoFetch = false, selectedDepartureDate, selectedReturnDate }) => {
    const [visibleMonths, setVisibleMonths] = useState([]);
    const lastReturnDateRef = useRef();
    const [forceRefreshKey, setForceRefreshKey] = useState(0);
    const dispatch = useDispatch();
    const searchbox = useSelector(getSearchbox);
    const isRoundTrip = searchbox.type === "ROUNDTRIP";
    const dispatchRef = useRef(dispatch);
    dispatchRef.current = dispatch;
    const prevEnabledRef = useRef(enabled);
    useEffect(() => {
        const currentReturnTime = selectedReturnDate?.getTime();
        if (currentReturnTime && currentReturnTime !== lastReturnDateRef.current) {
            lastReturnDateRef.current = currentReturnTime;
            dispatchRef.current(searchActions.fareCalendarClear());
            setForceRefreshKey((prev) => prev + 1);
        }
    }, [selectedReturnDate]);
    const { departureDateRange, returnDateRange } = useMemo(() => {
        if (visibleMonths.length === 0) {
            return { departureDateRange: undefined, returnDateRange: undefined };
        }
        const visibleRange = getDateRangeFromVisibleMonthDates(visibleMonths);
        if (isRoundTrip && selectedDepartureDate && !selectedReturnDate) {
            const formatDate = (date) => {
                const yyyy = date.getFullYear();
                const mm = String(date.getMonth() + 1).padStart(2, "0");
                const dd = String(date.getDate()).padStart(2, "0");
                return `${yyyy}-${mm}-${dd}`;
            };
            const departureDate = formatDate(selectedDepartureDate);
            const minReturnDate = departureDate;
            if (visibleRange[1] < minReturnDate) {
                return {
                    departureDateRange: [departureDate, departureDate],
                    returnDateRange: undefined
                };
            }
            const returnRangeStart = visibleRange[0] > minReturnDate ? visibleRange[0] : minReturnDate;
            return {
                departureDateRange: [departureDate, departureDate],
                returnDateRange: [returnRangeStart, visibleRange[1]]
            };
        }
        if (isRoundTrip && selectedDepartureDate && selectedReturnDate) {
            return {
                departureDateRange: visibleRange,
                returnDateRange: undefined
            };
        }
        return {
            departureDateRange: visibleRange,
            returnDateRange: undefined
        };
    }, [visibleMonths, isRoundTrip, selectedDepartureDate, selectedReturnDate]);
    const { data: fareCalendarData, loading: fetchLoading, error } = useFetchFareCalendar({ departureDateRange, returnDateRange, enabled, segmentIndex, forceRefreshKey });
    const flightsMap = useMemo(() => {
        if (!fareCalendarData) {
            return {};
        }
        if (mode !== "vertical") {
            const mergedData = {};
            Object.keys(fareCalendarData).forEach((key) => {
                const parts = key.split("_");
                if (parts.length === 2) {
                    Object.assign(mergedData, fareCalendarData[key]);
                }
            });
            return mergedData;
        }
        const mergedData = {};
        const isReturnMode = isRoundTrip && selectedDepartureDate && !selectedReturnDate;
        if (isReturnMode) {
            const departureDateStr = `${selectedDepartureDate.getFullYear()}-${String(selectedDepartureDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDepartureDate.getDate()).padStart(2, "0")}`;
            Object.keys(fareCalendarData).forEach((key) => {
                const parts = key.split("_");
                if (parts.length === 3 && parts[2] === departureDateStr) {
                    Object.assign(mergedData, fareCalendarData[key]);
                }
            });
        }
        else {
            Object.keys(fareCalendarData).forEach((key) => {
                const parts = key.split("_");
                if (parts.length === 2) {
                    Object.assign(mergedData, fareCalendarData[key]);
                }
            });
        }
        return mergedData;
    }, [fareCalendarData, mode, isRoundTrip, selectedDepartureDate, selectedReturnDate]);
    const priceMap = useMemo(() => {
        return createPriceMap(flightsMap);
    }, [flightsMap]);
    const handleMonthChange = useCallback((date) => {
        if (singleMonth) {
            const months = getSingleMonthDate(date);
            setVisibleMonths(months);
        }
        else if (mode === "vertical") {
            const months = getVisibleMonthDatesForMobile(date);
            setVisibleMonths(months);
        }
        else {
            const months = getVisibleMonthDates(date);
            setVisibleMonths(months);
        }
    }, [mode, singleMonth]);
    useEffect(() => {
        if (!enabled || disableAutoFetch) {
            return;
        }
        const dateToUse = baseDate || new Date();
        if (singleMonth) {
            const months = getSingleMonthDate(dateToUse);
            setVisibleMonths(months);
        }
        else if (mode === "vertical") {
            const months = getVisibleMonthDatesForMobile(dateToUse);
            setVisibleMonths(months);
        }
        else {
            const months = getVisibleMonthDates(dateToUse);
            setVisibleMonths(months);
        }
    }, [baseDate, mode, enabled, singleMonth, disableAutoFetch]);
    useEffect(() => {
        if (!enabled && prevEnabledRef.current === true) {
            dispatchRef.current(searchActions.fareCalendarClear());
        }
        prevEnabledRef.current = enabled;
    }, [enabled]);
    useEffect(() => {
        return () => {
            if (!disableAutoFetch) {
                dispatchRef.current(searchActions.fareCalendarClear());
            }
        };
    }, [disableAutoFetch]);
    const loading = enabled ? fetchLoading : false;
    return {
        priceMap,
        flightsMap,
        visibleMonths,
        handleMonthChange,
        loading,
        error
    };
};
